import React, { ReactNode, useEffect, useState } from "react";
import { isNil, isEmpty } from "ramda";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import Container from "@mui/material/Container";
import { Grid, Paper } from "@mui/material";
import Layout from "../../Layout";
import { ApplicationState } from "../../../store";
import {
  fetchRequest as fetchPos,
  saveRequest,
} from "../../../store/pos/actions";
import { fetchRequest as fetchPosSoftware } from "../../../store/possoftware/actions";
import Alert from "@mui/lab/Alert";
import { Pos, Data, PosState } from "../../../store/pos/types";
import pageStyles from "../../pageStyles";
import { withFormik, FormikProps } from "formik";
import useSession from "../../../effects/useSession";
import PageHeading from "../../../common/pageHeading/PageHeading";
import schema from "./posEdit.schema";

import { PosInterfaces } from "./PosConstants";
import SectionWrapper from "../../../common/pageSections/SectionWrapper";
import PosEditForm from "./PosEditForm";
import { toast } from "react-toastify";

const SaveError: React.FC<{ children: ReactNode }> = ({ children }) => (
  <Alert severity="error">{children}</Alert>
);

export default function PosEdit() {
  const dispatch = useDispatch();
  const history = useHistory();
  const classes = pageStyles();

  const { id } = useParams<{ id: string }>();

  const [formInput, setFormInput] = useState<Data | undefined>(undefined);

  const posState = useSelector((state: ApplicationState) => state.pos);

  const getById = (pos: PosState, id: string | undefined) => {
    return id && pos && pos.data && pos.data.data?.find((x) => x.id === id);
  };

  const pos = formInput ? formInput : (getById(posState, id) as Data);
  const session = useSession();

  useEffect(() => {
    dispatch(fetchPosSoftware(null));
    !isNil(id) && dispatch(fetchPos(id));
  }, []);

  // Time Diff is in milliseconds but is always rounded to a 30 minutes boundry. It is the local time of the POS compared to our server. Display in hours and minutes relative to UTC/GMT time. Label: “POS Time Offset from UTC”
  // Logged Out: Display a “Last Logout @ xxxx”. Display POS local date/time. If not defined, then “Last Logout @ Start-up”.

  const InnerForm = (props: FormikProps<Data>) => {
    return <PosEditForm />;
  };

  const EditForm = withFormik<Data, Data>({
    mapPropsToValues: (data) => {
      return !isEmpty(data)
        ? data
        : {
            id: null,
            type: "Pos",
            attributes: {
              POSDesc: "",
              Interface: PosInterfaces.NEXO,
              PrintOnPOS: true,
              TID: undefined,
              KEK: "",
              KEKIdentifier: "",
              KEKVersion: "",
            },
          };
    },
    handleSubmit: (values) => {
      const callback = (err: string | null, _data: Pos | null) => {
        if (err) {
          const error = JSON.parse(err)[0];
          let errText = "";
          if (
            error.detail.indexOf("E11000") === 0 &&
            error.detail.indexOf("attributes.POSDesc_1") > 0
          ) {
            errText =
              "Cannot create a new POS with duplicate description fields";
          } else {
            errText = error.detail;
          }
          toast.error(errText, {
            closeOnClick: true,
          });
        } else {
          if (id == null) {
            history.goBack();
          } else {
            toast.success("Saved successfully!", {
              closeOnClick: true,
            });
          }
        }
      };
      setFormInput(values);
      dispatch(saveRequest(values, callback));
    },

    validationSchema: schema,
  })(InnerForm);

  const headingText = () => {
    return id == null ? "New POS" : "Edit POS";
  };

  return (
    <Layout sessionData={session}>
      <main className={classes.content}>
        <div className={classes.appBarSpacer} />
        <Container maxWidth="lg" className={classes.container}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Paper>
                <PageHeading heading={headingText()} />
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Paper className={classes.paper}>
                <SectionWrapper title="POS">
                  <EditForm {...pos} />
                </SectionWrapper>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </main>
    </Layout>
  );
}
